import type {
    SICReportStatus,
    SICReportHistoryItem,
    SICAttentionItem,
    SICReportDetail,
    SICReportAccount,
} from '@/types/sic-report.types';

function isoDate(daysAgo: number): string {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString().split('T')[0] ?? '';
}

function isoTimestamp(daysAgo: number, hour = 9): string {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    d.setHours(hour, 0, 0, 0);
    return d.toISOString();
}

export const MOCK_STATUS: SICReportStatus = {
    report_id: 'mock-report-today',
    partner_id: 'UNE',
    run_date: isoDate(0),
    status: 'COMPLETED_WITH_ERRORS',
    started_at: isoTimestamp(0, 3),
    completed_at: isoTimestamp(0, 3),
    eligible_count: 124,
    skipped_count: 118,
    sent_count: 6,
    confirmed_count: 4,
    rejected_count: 1,
    error_count: 1,
    trigger: 'CRON',
    triggered_by: 'system',
};

export const MOCK_HISTORY: SICReportHistoryItem[] = [
    MOCK_STATUS,
    { ...MOCK_STATUS, report_id: 'mock-h-1', run_date: isoDate(1), status: 'COMPLETED',
      eligible_count: 122, skipped_count: 122, sent_count: 0, confirmed_count: 0, rejected_count: 0, error_count: 0,
      started_at: isoTimestamp(1, 3), completed_at: isoTimestamp(1, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-2', run_date: isoDate(2), status: 'COMPLETED_WITH_ERRORS',
      eligible_count: 120, skipped_count: 117, sent_count: 3, confirmed_count: 2, rejected_count: 1, error_count: 0,
      started_at: isoTimestamp(2, 3), completed_at: isoTimestamp(2, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-3', run_date: isoDate(3), status: 'COMPLETED',
      eligible_count: 118, skipped_count: 110, sent_count: 8, confirmed_count: 8, rejected_count: 0, error_count: 0,
      started_at: isoTimestamp(3, 3), completed_at: isoTimestamp(3, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-4', run_date: isoDate(4), status: 'FAILED',
      eligible_count: 0, skipped_count: 0, sent_count: 0, confirmed_count: 0, rejected_count: 0, error_count: 0,
      started_at: isoTimestamp(4, 3), completed_at: isoTimestamp(4, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-5', run_date: isoDate(5), status: 'COMPLETED',
      eligible_count: 116, skipped_count: 113, sent_count: 3, confirmed_count: 3, rejected_count: 0, error_count: 0,
      started_at: isoTimestamp(5, 3), completed_at: isoTimestamp(5, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-6', run_date: isoDate(6), status: 'COMPLETED',
      eligible_count: 115, skipped_count: 113, sent_count: 2, confirmed_count: 2, rejected_count: 0, error_count: 0,
      started_at: isoTimestamp(6, 3), completed_at: isoTimestamp(6, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-7', run_date: isoDate(7), status: 'COMPLETED_WITH_ERRORS',
      eligible_count: 110, skipped_count: 105, sent_count: 5, confirmed_count: 3, rejected_count: 2, error_count: 0,
      started_at: isoTimestamp(7, 3), completed_at: isoTimestamp(7, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-8', run_date: isoDate(8), status: 'COMPLETED',
      eligible_count: 105, skipped_count: 102, sent_count: 3, confirmed_count: 3, rejected_count: 0, error_count: 0,
      started_at: isoTimestamp(8, 3), completed_at: isoTimestamp(8, 3) },
    { ...MOCK_STATUS, report_id: 'mock-h-9', run_date: isoDate(9), status: 'COMPLETED',
      eligible_count: 100, skipped_count: 98, sent_count: 2, confirmed_count: 2, rejected_count: 0, error_count: 0,
      started_at: isoTimestamp(9, 3), completed_at: isoTimestamp(9, 3) },
];

const MOCK_ACCOUNT_BASE: SICReportAccount = {
    credit_id: 'CR-MOCK-001',
    directory_id: 'DIR-MOCK-001',
    report_type: 'UPDATE',
    credit_status: 'ACTIVE',
    payload_hash: 'a'.repeat(64),
    status: 'REJECTED',
    cdc_folio: null,
    error_code: 'VALIDATION_ERROR',
    error_message: 'RFC formato invalido',
    sent_at: isoTimestamp(0, 3),
    idempotency_key: 'LOANCO:CR-MOCK-001:' + isoDate(0),
    cdc_errors: { errores: [{ codigo: 'RFC_INVALIDO', mensaje: 'El RFC no cumple el formato' }] },
};

export const MOCK_ATTENTION: SICAttentionItem[] = [
    { ...MOCK_ACCOUNT_BASE, credit_id: 'CR-002', directory_id: 'DIR-002',
      error_message: 'RFC invalido', cdc_errors: { errores: [{ codigo: 'RFC_INVALIDO', mensaje: 'RFC invalido' }] } },
    { ...MOCK_ACCOUNT_BASE, credit_id: 'CR-009', directory_id: 'DIR-009',
      error_message: 'CP no valido', cdc_errors: { errores: [{ codigo: 'CP_INVALIDO', mensaje: 'CP debe tener 5 digitos' }] } },
    { ...MOCK_ACCOUNT_BASE, credit_id: 'CR-024', directory_id: 'DIR-024',
      error_message: 'CURP faltante', cdc_errors: { errores: [{ codigo: 'CURP_FALTANTE', mensaje: 'CURP requerido' }] } },
];

export function buildMockReportDetail(reportId: string, partnerId: string, runDate: string): SICReportDetail {
    const meta = MOCK_HISTORY.find((h) => h.report_id === reportId);
    if (!meta) {
        return {
            ...MOCK_STATUS,
            report_id: reportId, partner_id: partnerId, run_date: runDate,
            accounts: [],
        };
    }
    return {
        ...meta,
        accounts: buildMockAccounts(meta),
    };
}

function buildMockAccounts(meta: SICReportStatus): SICReportAccount[] {
    const accounts: SICReportAccount[] = [];
    for (let i = 0; i < meta.confirmed_count; i++) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { cdc_errors: _omit, ...baseWithoutCdcErrors } = MOCK_ACCOUNT_BASE;
        accounts.push({
            ...baseWithoutCdcErrors,
            credit_id: `CR-${meta.report_id}-${i + 1}`,
            status: 'CONFIRMED',
            cdc_folio: `CDC-FOLIO-${meta.report_id}-${i + 1}`,
            error_code: null,
            error_message: null,
        });
    }
    for (let i = 0; i < meta.rejected_count; i++) {
        accounts.push({
            ...MOCK_ACCOUNT_BASE,
            credit_id: `CR-${meta.report_id}-rej-${i + 1}`,
            status: 'REJECTED',
        });
    }
    for (let i = 0; i < meta.error_count; i++) {
        accounts.push({
            ...MOCK_ACCOUNT_BASE,
            credit_id: `CR-${meta.report_id}-err-${i + 1}`,
            status: 'ERROR',
            error_code: 'PROVIDER_ERROR',
            error_message: 'CDC sandbox no respondio a tiempo',
        });
    }
    return accounts;
}
